import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Bookmark, Save, Trash2, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable saved views selector.
 * Props:
 *  - page: string  — identifier stored with the view (e.g. "defects").
 *  - currentFilters: object — the current filter state (JSON-serialisable).
 *  - onApply(filters): callback the parent uses to restore filters.
 *  - activeName?: string — display name of the currently applied view, if any.
 *  - onClearActive?: () => void — parent may call this when "Effacer la vue" is pressed.
 */
export default function SavedViews({ page, currentFilters, onApply, activeName, onClearActive }) {
  const qc = useQueryClient();
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const { data: views = [] } = useQuery({
    queryKey: ["saved-views", page],
    queryFn: () => api.get("/saved-views", { params: { page } }).then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: () => api.post("/saved-views", { name, page, filters: currentFilters }),
    onSuccess: () => { toast.success(`Vue « ${name} » sauvegardée`); qc.invalidateQueries({ queryKey: ["saved-views", page] }); setSaveOpen(false); setName(""); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/saved-views/${id}`),
    onSuccess: () => { toast.success("Vue supprimée"); qc.invalidateQueries({ queryKey: ["saved-views", page] }); },
    onError: (e) => toast.error(extractError(e, "Échec de la suppression")),
  });

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`saved-views-${page}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="saved-views-btn">
            <Bookmark size={13} className="mr-1.5" />
            {activeName ? <>Vue : <span className="font-semibold ml-1 truncate max-w-[160px]">{activeName}</span></> : "Vues sauvegardées"}
            <ChevronDown size={13} className="ml-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Mes vues ({views.length})</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {views.length === 0 && <div className="px-2 py-3 text-xs text-slate-400 text-center">Aucune vue enregistrée.<br />Filtrez puis « Sauvegarder ».</div>}
          {views.map((v) => (
            <DropdownMenuItem key={v.id} className="flex items-center justify-between gap-2 group" onSelect={(e) => e.preventDefault()}>
              <button
                data-testid={`view-apply-${v.id}`}
                onClick={() => { onApply(v.filters, v.name); toast.success(`Vue « ${v.name} » appliquée`); }}
                className="flex-1 text-left truncate text-sm"
                title={JSON.stringify(v.filters)}
              >{v.name}</button>
              <button
                data-testid={`view-delete-${v.id}`}
                onClick={(e) => { e.stopPropagation(); remove.mutate(v.id); }}
                className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition"
                title="Supprimer"
              ><Trash2 size={12} /></button>
            </DropdownMenuItem>
          ))}
          {activeName && onClearActive && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="view-clear-active" onClick={onClearActive} className="text-xs text-slate-500">
                <X size={12} className="mr-1.5" /> Effacer la vue active
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="view-save-current" onClick={() => setSaveOpen(true)} className="font-semibold text-blue-700">
            <Save size={12} className="mr-1.5" /> Sauvegarder la vue actuelle
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Sauvegarder la vue</DialogTitle>
            <DialogDescription>Les filtres et l&apos;onglet actuels seront enregistrés sous ce nom. Vous pourrez les restaurer en un clic.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nom de la vue</Label>
            <Input data-testid="save-view-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Désordres GPA critiques" autoFocus onKeyDown={(e) => e.key === "Enter" && name.trim() && save.mutate()} />
            <pre className="text-[10px] font-mono text-slate-400 bg-slate-50 p-2 rounded max-h-24 overflow-auto">{JSON.stringify(currentFilters, null, 2)}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Annuler</Button>
            <Button data-testid="save-view-confirm" className="text-white" style={{ background: "#023E73" }} disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
